// Emailing a PO from Gmail (ERP-PLAN §5.5): "Send to vendor" opens a compose dialog filled in at once from synced
// rows (the vendor's PO recipients, a subject with the PO number and revision, an English or Chinese body with the
// documents link, the PDF attached); sending posts to /api/po/:id/email, which marks the PO sent. The strip under the
// PO header shows the last send, the vendor's latest reply on its thread (Open in Gmail) and their acknowledgement.
import { Check, ExternalLink, Mail, MailCheck, Paperclip, RefreshCw, Send, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { Po, PoEmail, Vendor } from "../../../../shared/db.gen.ts";
import { headerEditable } from "../../../../shared/po.ts";
import { chooseSender, composeDefaults, DOCS_PLACEHOLDER, gmailThreadUrl, parseAddresses, poStamp } from "../../../../shared/po-email.ts";
import { type DraftCheck, needsWarning } from "../../../../shared/po-plan-check.ts";
import { ago, personName } from "../../lib/format.ts";
import { can, mutateWithResult } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { me } from "../../lib/session.ts";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Select, Textarea } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";
import { useLinesByPo } from "./lib.ts";
import { LabelGapsBanner, PlanCheckBanner, PrintGapsBanner, sendAnyway } from "./PoPlanCheck.tsx"; // Check against the plan ---
import { type LabelGap, type PrintGap, useOpenFix, useOpenLabel } from "./packaging.ts"; // Packaging ---

/** Posts JSON to the API the way mutations do (the header blocks cross-site posts). */
export async function postJson<T>(url: string, body: unknown = {}): Promise<{ ok: boolean; body: T & { error?: string } }> {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify(body),
    });
    return { ok: res.ok, body: (await res.json().catch(() => ({}))) as T & { error?: string } };
  } catch {
    return { ok: false, body: { error: "Couldn't reach HHL Ops" } as T & { error?: string } };
  }
}

/** What an admin must do before POs can be emailed, in words. */
export function gmailOffText() {
  const missing = me()?.gmail?.missing ?? [];
  return `Emailing POs from Gmail isn't turned on yet. An admin adds the Gmail scopes to the Google Cloud consent screen and sets ${
    missing.join(", ") || "GMAIL_SEND=1"
  } on the server (README → “Sending POs from Gmail”). Until then, email the PDF yourself and use Mark as sent.`;
}

/** The PO's emails and replies, newest first. */
function usePoEmails(poId: string) {
  const emails = useRows("po_email");
  const replies = useRows("po_email_reply");
  return useMemo(
    () => ({
      emails: emails.filter((e) => e.po_id === poId).sort((a, b) => b.started_at.localeCompare(a.started_at)),
      replies: replies.filter((r) => r.po_id === poId).sort((a, b) => b.received_at.localeCompare(a.received_at)),
    }),
    [emails, replies, poId],
  );
}

/** The header button: opens the compose dialog, or says what's missing first. */
/**
 * Check against the plan --- `check` (drafts): the compose dialog warns when the draft differs from the plan; `getCheck`
 * brings it up to date when the dialog opens; `review` opens the check. Packaging --- `gaps`: packaging parts whose
 * revision lacks print files (usePoPacking()), a second warning. Product labels --- `labelGaps`: products whose label has
 * something off (usePoLabels()), a third.
 */
type WarnProps = {
  check?: DraftCheck | null;
  getCheck?: () => DraftCheck | null;
  review?: () => void;
  gaps?: readonly PrintGap[];
  labelGaps?: readonly LabelGap[];
};

export function SendToVendorButton({
  po,
  vendor,
  primary,
  check,
  getCheck,
  review,
  gaps,
  labelGaps,
}: { po: Po; vendor: Vendor | undefined; primary: boolean } & WarnProps) {
  const [open, setOpen] = useState(false);
  const enabled = !!me()?.gmail?.enabled;
  if (!can("po.mark_sent") || !headerEditable(po.status)) return null;
  const again = po.sent_revision !== null && po.sent_revision === po.revision;
  return (
    <>
      <Button
        size="sm"
        variant={primary ? "primary" : "secondary"}
        icon={<Mail className="size-3.5" />}
        title={enabled ? "Email the PO PDF to the vendor from Gmail; it's marked as sent when it goes" : gmailOffText()}
        onClick={() => {
          if (!enabled) return toast.info(gmailOffText());
          getCheck?.();
          setOpen(true);
        }}
      >
        {again ? "Email again" : po.sent_revision === null ? "Send to vendor" : `Send rev ${po.revision}`}
      </Button>
      {open && vendor && <SendDialog po={po} vendor={vendor} onClose={() => setOpen(false)} check={check} review={review} gaps={gaps} labelGaps={labelGaps} />}
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-16 shrink-0 pt-1.5 text-right text-[12px] text-muted">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function SendDialog({ po, vendor, onClose, check, review, gaps = [], labelGaps = [] }: { po: Po; vendor: Vendor; onClose: () => void } & WarnProps) {
  const user = me();
  // Check against the plan --- differences staff haven't gone through yet: a warning, and Send becomes "Send anyway".
  const warn = check && needsWarning(check, po.plan_check_key) ? check : null;
  // Packaging --- the same for packaging parts the vendor can't print from what's released, and Product labels --- for
  // labels with something off (neither recorded on the PO).
  const anyway = !!warn || gaps.length > 0 || labelGaps.length > 0;
  const openFix = useOpenFix();
  const openLabel = useOpenLabel();
  const contacts = useRows("vendor_contact");
  const allMailboxes = useRows("gmail_account");
  const users = useRows("app_user");
  // A mailbox whose owner no longer has access can't be sent from.
  const mailboxes = useMemo(() => allMailboxes.filter((m) => users.find((u) => u.id === m.user_id)?.active), [allMailboxes, users]);
  const settings = useRows("po_email_settings")[0];
  const lines = useLinesByPo().get(po.id);
  // The PO as this dialog shows it: if it changes before Send (even a draft's lines), the server asks to look again.
  const [stamp] = useState(() => poStamp(po, lines ?? []));
  const sender = useMemo(
    () =>
      chooseSender({
        vendorSender: vendor.po_sender_email,
        defaultSender: settings?.default_sender_email ?? "",
        user: { id: user?.id ?? "", email: user?.email ?? "" },
        mailboxes,
      }),
    [vendor.po_sender_email, settings?.default_sender_email, user, mailboxes],
  );
  const [accountId, setAccountId] = useState(sender.mailbox?.id ?? sender.options[0]?.id ?? "");
  const from = mailboxes.find((m) => m.id === accountId) ?? null;
  // Filled in once when the dialog opens (the sign-off uses the sender's first name); everything stays editable.
  const [draft] = useState(() =>
    composeDefaults({
      po,
      vendor,
      contacts: contacts.filter((c) => c.vendor_id === vendor.id),
      sender: from ? { name: from.name || user?.name || "", email: from.email } : user ? { name: user.name, email: user.email } : null,
    }),
  );
  const [to, setTo] = useState(draft.to.join(", "));
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [busy, setBusy] = useState(false);
  // One send per dialog: retrying after an error can never email the vendor twice.
  const [sendId] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (!accountId && sender.options[0]) setAccountId(sender.options[0].id);
  }, [accountId, sender.options]);

  const toParsed = parseAddresses(to);
  const ccParsed = parseAddresses(cc);
  const bad = [...toParsed.bad, ...ccParsed.bad];
  const ready = !!from && toParsed.emails.length > 0 && !bad.length && subject.trim() && body.trim() && !busy;
  const pdfUrl = `/api/po/${po.id}/pdf?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`;

  const send = async () => {
    if (!ready || !from) return;
    setBusy(true);
    const { ok, body: res } = await postJson<{ warning?: string; already?: boolean }>(`/api/po/${po.id}/email`, {
      id: sendId,
      account_id: from.id,
      to: toParsed.emails,
      cc: ccParsed.emails,
      subject: subject.trim(),
      body,
      stamp,
      revision: po.revision,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(warn ? { plan_check: sendAnyway(warn) } : {}),
    });
    setBusy(false);
    if (!ok) return toast.error(res.error ?? "It didn't send");
    if (res.warning) toast.info(res.warning);
    else toast.ok(`${po.number} sent to ${toParsed.emails.join(", ")}`);
    onClose();
  };

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !busy) {
            e.stopPropagation();
            onClose();
          }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
        }}
        className="flex max-h-[84vh] w-[680px] flex-col rounded-xl border border-line bg-surface shadow-pop"
      >
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Mail className="size-4 text-muted" />
          <span className="text-[15px] font-semibold">
            Send {po.number}
            {po.revision ? ` rev ${po.revision}` : ""} to {vendor.short_name || vendor.name}
          </span>
          {draft.language === "zh" && <Badge>中文</Badge>}
          <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-muted hover:bg-hover" title="Close (Esc)">
            <X className="size-4" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 py-3">
          {warn && (
            <PlanCheckBanner
              check={warn}
              onReview={() => {
                onClose();
                review?.();
              }}
            />
          )}
          {gaps.length > 0 && (
            <PrintGapsBanner
              gaps={gaps}
              onOpen={(g) => {
                onClose();
                openFix(g);
              }}
            />
          )}
          {labelGaps.length > 0 && (
            <LabelGapsBanner
              gaps={labelGaps}
              onOpen={(g) => {
                onClose();
                openLabel(g);
              }}
            />
          )}
          <Row label="From">
            {sender.options.length ? (
              <Select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                options={sender.options.map((m) => ({ value: m.id, label: m.name ? `${m.name} <${m.email}>` : m.email }))}
                className="h-8"
              />
            ) : (
              <div className="pt-1.5 text-danger">
                {sender.problem}{" "}
                <Link to="/settings/email" className="text-accent-text hover:underline">
                  Settings → Email
                </Link>
              </div>
            )}
            {sender.problem && sender.options.length > 0 && <div className="mt-1 text-[12px] text-warn">{sender.problem}</div>}
          </Row>
          <Row label="To">
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="The vendor's PO recipients (comma between addresses)"
              autoFocus={!draft.to.length}
            />
            {!draft.to.length && (
              <div className="mt-1 text-[12px] text-warn">
                {vendor.short_name || vendor.name} has no contact marked as a PO recipient; type an address, or mark one in the vendor's contacts.
              </div>
            )}
          </Row>
          <Row label="Cc">
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Optional" />
          </Row>
          {bad.length > 0 && <div className="ml-[76px] text-[12px] text-danger">Not an email address: {bad.join(", ")}</div>}
          <Row label="Subject">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Row>
          <Row label="Message">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={13} className="min-h-64 font-[inherit] leading-relaxed" />
            <div className="mt-1 text-[12px] text-faint">{DOCS_PLACEHOLDER} becomes this PO's documents page link (specs, drawings and files).</div>
          </Row>
          <Row label="Attached">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 pt-1.5 text-accent-text hover:underline">
              <Paperclip className="size-3.5" /> {draft.attachment}
            </a>
          </Row>
        </div>
        <div className="flex items-center gap-2 border-t border-line px-4 py-3">
          <span className="flex-1 text-[12px] text-muted">
            {from ? `From ${from.email}. Sending marks ${po.number} as sent.` : "Connect a Gmail mailbox first."}
          </span>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" icon={<Send className="size-3.5" />} disabled={!ready} kbd="Ctrl ↵">
            {busy ? "Sending…" : anyway ? "Send anyway" : "Send"}
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}

/** Under the PO header: the last email, the vendor's latest reply, and their acknowledgement. */
export function PoEmailStrip({ po, vendor, check: planCheck, getCheck, review, gaps, labelGaps }: { po: Po; vendor: Vendor | undefined } & WarnProps) {
  const { emails, replies } = usePoEmails(po.id);
  const users = useRows("app_user");
  const [acking, setAcking] = useState(false);
  const [resend, setResend] = useState(false);
  const [checking, setChecking] = useState(false);
  const last = emails[0];
  const sent = emails.find((e) => e.status === "sent");
  const reply = replies[0];
  const replyMailbox = emails.find((e) => e.id === reply?.po_email_id)?.from_email ?? sent?.from_email ?? "";
  const canAck = can("po.acknowledge") && (po.status === "sent" || po.status === "acknowledged" || po.status === "in_production");
  if (!last && !po.acknowledged_at && !(canAck && po.status === "sent")) return null;
  const who = (id: string | null) => personName(users, id);

  const check = async () => {
    setChecking(true);
    const { ok, body } = await postJson<{ linked: number }>(`/api/po/${po.id}/email/check`);
    setChecking(false);
    if (!ok) toast.error(body.error ?? "Couldn't check Gmail");
    else toast.ok(body.linked ? `${body.linked} new ${body.linked === 1 ? "reply" : "replies"}` : "No new replies");
  };

  return (
    <div className="flex flex-col gap-1 border-b border-line bg-surface-2/40 px-4 py-2 text-[12px]">
      {last && (
        <div className="flex items-center gap-2">
          <MailCheck className={cx("size-3.5 shrink-0", last.status === "sent" ? "text-ok" : last.status === "failed" ? "text-danger" : "text-warn")} />
          <span className="min-w-0 flex-1 truncate">
            {last.status === "sent" ? (
              <>
                Emailed{last.revision ? ` rev ${last.revision}` : ""} to {last.to_emails.join(", ")} from {last.from_email} by {who(last.sent_by)},{" "}
                {ago(last.sent_at)}
              </>
            ) : last.status === "failed" ? (
              <span className="text-danger">Last email didn't go: {last.error}</span>
            ) : (
              <span className="text-warn" title={last.error}>
                {last.error ? `We can't tell whether the last email went. Check Sent in ${last.from_email}: ${last.error}` : "Sending…"}
              </span>
            )}
          </span>
          {sent?.gmail_thread_id && (
            <a
              href={gmailThreadUrl(sent.from_email, sent.gmail_thread_id)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-0.5 text-accent-text hover:underline"
            >
              Open in Gmail <ExternalLink className="size-3" />
            </a>
          )}
          {last.status === "failed" && vendor && can("po.mark_sent") && (
            <button
              type="button"
              className="shrink-0 text-accent-text hover:underline"
              onClick={() => {
                getCheck?.();
                setResend(true);
              }}
            >
              Try again
            </button>
          )}
          {last.status === "sending" && last.error && can("po_email.resolve") && <UnknownSend email={last} />}
          {sent && me()?.gmail?.enabled && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 text-muted hover:text-text"
              onClick={check}
              disabled={checking}
              title="Look for vendor replies now (HHL Ops checks every 10 minutes)"
            >
              <RefreshCw className={cx("size-3", checking && "animate-spin")} /> Replies
            </button>
          )}
        </div>
      )}
      {reply && (
        <div className="flex items-center gap-2">
          <Mail className="size-3.5 shrink-0 text-accent-text" />
          <span className="min-w-0 flex-1 truncate">
            <span className="font-medium">{reply.from_name || reply.from_email}</span> replied {ago(reply.received_at)}
            {replies.length > 1 ? ` (${replies.length} replies)` : ""}
            {reply.snippet && <span className="text-muted">: “{reply.snippet}”</span>}
          </span>
          <a
            href={gmailThreadUrl(replyMailbox, reply.gmail_thread_id)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center gap-0.5 text-accent-text hover:underline"
          >
            Open in Gmail <ExternalLink className="size-3" />
          </a>
        </div>
      )}
      {(po.acknowledged_at || (canAck && po.status === "sent")) && (
        <div className="flex items-center gap-2">
          <Check className={cx("size-3.5 shrink-0", po.acknowledged_at ? "text-ok" : "text-faint")} />
          <span className="min-w-0 flex-1 truncate">
            {po.acknowledged_at ? (
              <>
                Vendor confirmed qty, price and dates{po.vendor_ref ? ` (${po.vendor_ref})` : ""}, recorded by {who(po.acknowledged_by)}{" "}
                {ago(po.acknowledged_at)}
                {po.ack_note && <span className="text-muted">: {po.ack_note}</span>}
              </>
            ) : (
              <span className="text-muted">Waiting for the vendor to confirm qty, price and dates.</span>
            )}
          </span>
          {canAck && (
            <button type="button" className="shrink-0 text-accent-text hover:underline" onClick={() => setAcking(true)}>
              {po.acknowledged_at ? "Edit" : "Vendor confirmed"}
            </button>
          )}
        </div>
      )}
      {acking && <AcknowledgeDialog po={po} onClose={() => setAcking(false)} />}
      {resend && vendor && (
        <SendDialog po={po} vendor={vendor} onClose={() => setResend(false)} check={planCheck} review={review} gaps={gaps} labelGaps={labelGaps} />
      )}
    </div>
  );
}

/**
 * An email Gmail may or may not have sent: look in Sent again (the same send, so never a second copy), or say what
 * Sent shows: it went (the PO is marked sent) or it didn't (then a new send can go).
 */
function UnknownSend({ email }: { email: PoEmail }) {
  const [busy, setBusy] = useState(false);
  const again = async () => {
    setBusy(true);
    const { ok, body } = await postJson<{ warning?: string }>(`/api/po/${email.po_id}/email`, {
      id: email.id,
      account_id: email.account_id,
      to: email.to_emails,
      cc: email.cc_emails,
      subject: email.subject,
      body: email.body,
      stamp: email.content_stamp,
    });
    setBusy(false);
    if (!ok) toast.error(body.error ?? "Still can't tell");
    else toast.ok(body.warning ?? "Found it in Gmail: it went");
  };
  const resolve = async (went: boolean) => {
    const q = went ? "It's in Sent in Gmail? The PO will be marked as sent." : "It's not in Sent in Gmail? Then you can send it again.";
    if (!confirm(q)) return;
    const { ok, result } = await mutateWithResult("po_email.resolve", { id: email.id, went });
    const warning = (result as { warning?: string } | undefined)?.warning;
    if (ok && warning) toast.info(warning);
  };
  return (
    <span className="inline-flex shrink-0 items-center gap-2">
      <button type="button" className="text-accent-text hover:underline" disabled={busy} onClick={again}>
        {busy ? "Looking…" : "Look again"}
      </button>
      <button type="button" className="text-accent-text hover:underline" onClick={() => resolve(true)}>
        It went
      </button>
      <button type="button" className="text-accent-text hover:underline" onClick={() => resolve(false)}>
        It didn't go
      </button>
    </span>
  );
}

/** Records the vendor's confirmation: their ref, ship date and a note. Sent → acknowledged. */
export function AcknowledgeDialog({ po, onClose }: { po: Po; onClose: () => void }) {
  const [ref, setRef] = useState(po.vendor_ref);
  const [ship, setShip] = useState(po.expected_ship ?? "");
  const [note, setNote] = useState(po.ack_note);
  const save = async () => {
    const status = po.status === "sent" ? "acknowledged" : po.status;
    const { ok } = await mutateWithResult("po.acknowledge", { id: po.id, vendor_ref: ref.trim(), expected_ship: ship || null, note: note.trim() }, [
      {
        t: "po",
        id: po.id,
        row: {
          ...po,
          status,
          vendor_ref: ref.trim(),
          expected_ship: ship || null,
          ack_note: note.trim(),
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: me()?.id ?? null,
        },
      },
    ]);
    if (ok) onClose();
  };
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-[440px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="text-[15px] font-semibold">The vendor confirmed {po.number}</div>
        <p className="mt-1 text-[12px] text-muted">Their quantities, prices and dates. Change lines that differ in the PO itself.</p>
        <div className="mt-3 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Their ref (PI or order confirmation)</span>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} autoFocus />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Ships by</span>
            <Input type="date" value={ship} onChange={(e) => setShip(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Note</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Lily confirmed on WeChat" />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" kbd="↵">
            {po.status === "sent" ? "Mark acknowledged" : "Save"}
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}
